import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import './ContactAtacama.css';

const ContactAtacama: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    checkIn: '',
    checkOut: '',
    adults: '',
    children: '',
    interests: [] as string[],
    specialRequests: ''
  });

  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <div className="contact-atacama-page">
      <UnifiedNavigation variant="transparent" destination="atacama" />
      
      {/* Hero Section with Background Image */}
      <section className="contact-hero" data-scroll-section="hero">
        <div className="contact-hero__overlay"></div>
        <img 
          src={images.atacamaHero || images.desertLandscape} 
          alt="Atacama Desert" 
          className="contact-hero__image"
        />
        
        {/* Contact Form Overlay */}
        <div className="contact-form-overlay">
          <div className="contact-form-container">
            <h1 className="contact-form__title">Contact Us</h1>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">FIRST NAME*</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">LAST NAME*</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">EMAIL*</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">PHONE NUMBER</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="country">COUNTRY</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                >
                  <option value="">Select your country</option>
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="BR">Brazil</option>
                  <option value="CL">Chile</option>
                  <option value="AR">Argentina</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="checkIn">CHECK IN DATE</label>
                  <input
                    type="date"
                    id="checkIn"
                    name="checkIn"
                    value={formData.checkIn}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="checkOut">CHECK OUT DATE</label>
                  <input
                    type="date"
                    id="checkOut"
                    name="checkOut"
                    value={formData.checkOut}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="adults">NUMBER OF ADULTS</label>
                  <select
                    id="adults"
                    name="adults"
                    value={formData.adults}
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="children">NUMBER OF CHILDREN</label>
                  <select
                    id="children"
                    name="children"
                    value={formData.children}
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    {[0, 1, 2, 3, 4].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group full-width">
                <label>WHAT ARE YOU INTERESTED IN?</label>
                <div className="interests-grid">
                  {['Nature', 'Adventure', 'Culture', 'Relaxation', 'Gastronomy', 'Photography'].map(interest => (
                    <label key={interest} className="interest-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(interest)}
                        onChange={() => handleInterestToggle(interest)}
                      />
                      <span>{interest}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="specialRequests">ADDITIONAL INFORMATION</label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Tell us about your special requests..."
                />
              </div>
              
              <button type="submit" className="submit-button">
                SUBMIT
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section" data-scroll-section="info">
        <div className="contact-info-container">
          <div className="contact-info-grid">
            <div className="contact-info-column">
              <h2>Reservations</h2>
              <p className="contact-email">reservations@awasi.com</p>
              <h3>Toll Free</h3>
              <p>US: 1 (855) 332-6183</p>
              <p>UK: 0 800 088 5428</p>
              <p>DE: 0 800 588 1348</p>
              <p>BR: 0 800 591 1723</p>
            </div>
            
            <div className="contact-info-column">
              <h2>Latin America</h2>
              <p>CL: +56 2 2233 9641</p>
              <p>BR: 0 800 588 1348</p>
              <p>AR: +54 11 5368 4878</p>
            </div>
            
            <div className="contact-info-column">
              <h2>Awasi Atacama</h2>
              <p>Tocopilla 551<br />
              San Pedro de Atacama<br />
              Antofagasta, Chile</p>
              <p className="contact-phone">CL: +56 55 285 1641</p>
            </div>
          </div>
          
          <div className="social-section">
            <h2>Social Inquiries</h2>
            <p>awasi@awasi.cl</p>
            <div className="social-links">
              <a href="https://www.instagram.com/awasiexperience/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">INSTAGRAM</a>
              <a href="https://www.linkedin.com/company/awasi/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">LINKEDIN</a>
              <a href="https://www.youtube.com/channel/UCAwasi" target="_blank" rel="noopener noreferrer" aria-label="YouTube">YOUTUBE</a>
            </div>
          </div>
          
          <div className="partners-section">
            <h2>Social Partners</h2>
            <p>
              Exclusive Partner Hotels from the Awasi team, sharing our same 
              values of remote luxury settings, sustainability.
            </p>
            <div className="partner-links">
              <a href="https://umanlodge.com/" target="_blank" rel="noopener noreferrer">UMAN LODGE</a>
              <a href="https://awasiafrica.com/" target="_blank" rel="noopener noreferrer">AWASI AFRICA</a>
            </div>
          </div>
          
          <div className="members-section">
            <h2>Social Members</h2>
            <p>
              Proudly B Corp Certified and members of prestigious associations:
            </p>
            <div className="member-logos">
              <img src={images.bcorpLogo || images.instagram1} alt="B Corp" />
              <img src={images.relaisLogo || images.instagram2} alt="Relais & Châteaux" />
              <img src={images.virtuosoLogo || images.instagram3} alt="Virtuoso" />
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="contact-instagram-section" data-scroll-section="instagram">
        <h2>Follow us on Instagram as we manage our properties</h2>
        <div className="instagram-grid">
          <img src={images.contactInsta1 || images.instagram1} alt="Instagram 1" />
          <img src={images.contactInsta2 || images.instagram2} alt="Instagram 2" />
          <img src={images.contactInsta3 || images.instagram3} alt="Instagram 3" />
          <img src={images.contactInsta4 || images.instagram4} alt="Instagram 4" />
        </div>
      </section>

      {/* Destinations Section */}
      <section className="contact-destinations-section" data-scroll-section="destinations">
        <h2>Destinations</h2>
        <div className="destinations-grid">
          <div className="destination-card">
            <img src={images.patagoniaCard} alt="Patagonia" />
            <div className="destination-info">
              <h3>AWASI PATAGONIA</h3>
              <p>TORRES DEL PAINE</p>
              <a onClick={() => navigate('/patagonia')} style={{ cursor: 'pointer' }}>DISCOVER</a>
            </div>
          </div>
          
          <div className="destination-card">
            <img src={images.iguazuCard} alt="Iguazu" />
            <div className="destination-info">
              <h3>AWASI IGUAZU</h3>
              <p>ATLANTIC RAINFOREST</p>
              <a onClick={() => navigate('/iguazu')} style={{ cursor: 'pointer' }}>DISCOVER</a>
            </div>
          </div>
        </div>
      </section>

      <Footer variant="atacama" />
    </div>
  );
};

export default ContactAtacama;